import aws4 from 'aws4';
import Config from 'Config';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uuidv4 from 'uuid/v4';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import Dialog from 'components/DialogWrapper/DialogWrapper';

import MapIcon from 'mdi-material-ui/MapMarker';

import { makeStyles, Typography } from '@material-ui/core';
import Button from 'components/Button/Button';
import ButtonLoading from 'components/ButtonLoading/ButtonLoading';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import ProfileImage from 'scenes/Settings/components/Profile/ProfileImage';
import { createFilePermissions } from 'store/filepermissions/thunks/createFilePermissions';
import { closeProfilePictureRequiredDialog } from 'store/records/actions/records.actions';
import { selectLoggedInUserPersonalResource } from 'store/resources/selectors/resources.selectors';
import { setUpdateResourcesFromSettingProfile } from 'store/resources/thunks/setUpdateResourcesFromSettingProfile.thunk';
import fetchFile from 'utils/fetchFile';
import getCredentials from 'utils/getCredentials';
import readAndCompress from 'utils/readAndCompress';
import toBase64 from 'utils/toBase64';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    ...theme.component.dialogPaper,
    [theme.breakpoints.down('xs')]: {
      ...theme.component.dialogPaperFullscreen,
    },
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  dialogActions: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
}));

const head = 'data:image/jpeg;base64,';

export const ProfilePictureRequiredDialog = () => {
  const classes = useStyles();

  const [loadingProfileImage, setLoadingProfileImage] = useState(true);
  const [profileImageDataUri, setProfileImageDataUri] = useState('');
  const [profileImageFilename, setProfileImageFilename] = useState('');
  const [profileImageOriginalFilename, setProfileImageOriginalFilename] =
    useState('');
  const [profileImageUpdate, setProfileImageUpdate] = useState(false);
  const [profileImageRequest, setProfileImageRequest] = useState(null);
  const [src, setSrc] = useState('');
  const [profileImageModified, setProfileImageModified] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const { t } = useTranslation('common');

  const userPersonalResource = useSelector(selectLoggedInUserPersonalResource);

  const dispatch = useDispatch();

  const onClose = () => {
    dispatch(closeProfilePictureRequiredDialog());
  };

  useEffect(() => {
    getProfileImageSrc();
  }, []);

  useEffect(() => {
    if (profileImageDataUri) {
      handleProfileImageRequest();
    }
  }, [profileImageDataUri]);

  const getProfileImageSrc = () => {
    if (userPersonalResource.picture && userPersonalResource.picture.key) {
      const key = userPersonalResource.picture.key;
      const filename = key.split('/').pop();
      setLoadingProfileImage(true);
      fetchFile(key, {}, true)
        .then((url) => {
          if (url) {
            setProfileImageFilename(filename);
            setProfileImageOriginalFilename(filename);
            setSrc(url);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      setSrc('');
    }
    setLoadingProfileImage(false);
  };

  const fileToDataUri = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve(event.target.result);
      };
      reader.readAsDataURL(file);
    });

  const handleProfileImageChange = async (event) => {
    let file = event.target.files[0];
    const name = file.name;

    let size = file.size;
    while (size > 4380000) {
      file = await readAndCompress(file);
      size = ((file.length - head.length) * 3) / 4;
    }

    if (typeof file !== 'string') {
      fileToDataUri(file).then((dataUri) => {
        setProfileImageUpdate(() => true);
        setProfileImageOriginalFilename(() => name);
        setProfileImageDataUri(() => dataUri);
      });
    } else {
      setProfileImageUpdate(() => true);
      setProfileImageOriginalFilename(() => name);
      setProfileImageDataUri(() => file);
    }
    setProfileImageModified(true);
  };

  const handleCroppedImageSave = async (file) => {
    let base64file = await fileToDataUri(file);
    setProfileImageUpdate(() => true);
    setProfileImageDataUri(() => base64file);
    setProfileImageModified(true);
  };

  const handleProfileImageRequest = async () => {
    const res = await fetch(profileImageDataUri);
    const blob = await res.blob();

    let extension = '';
    try {
      extension = profileImageOriginalFilename.split('.').pop();
    } catch {
      extension = '';
    }
    const file = new File([blob], profileImageOriginalFilename, {
      type: 'image/' + extension,
    });

    try {
      let fileContents = null;
      try {
        fileContents = await toBase64(file);
      } catch (e) {
        console.warn(e.message);
      }
      const uuid = uuidv4();
      const filename = `${uuid}.${extension}`;
      const payload = { name: filename, file: fileContents };
      const path = `${Config.S3_PROFILE_IMAGES_DIR}/x/`;
      const filesDomain = Config.FILES_DOMAIN;
      const request = {
        host: filesDomain,
        path: path,
        method: 'POST',
        region: Config.AWS_REGION,
        service: 'execute-api',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      };
      setProfileImageRequest(() => request);
      setProfileImageFilename(() => filename);
      setProfileImageModified(true);
    } catch (err) {
      console.log(err);
    }
  };

  const handleProfileImageRotate = (imageSrc) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      canvas.width = image.height;
      canvas.height = image.width;
      context.save();
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate((90 * Math.PI) / 180);

      context.drawImage(image, -image.width / 2, -image.height / 2);

      context.restore();

      canvas.toBlob((blob) => {
        setProfileImageUpdate(true);
        setProfileImageDataUri(URL.createObjectURL(blob));
      });
    };
    context.drawImage(image, 0, 0);
    setProfileImageModified(true);
  };

  const createFilePermissionsForImages = async (filePermissions) => {
    if (!filePermissions) {
      return;
    }
    dispatch(createFilePermissions(filePermissions));
  };

  const handleSave = async () => {
    setSaveLoading(true);

    let profileData = {
      ...userPersonalResource,
    };

    if (profileImageUpdate && profileImageDataUri) {
      const { accessKeyId, secretAccessKey, sessionToken } =
        await getCredentials();
      const signer = new aws4.RequestSigner(profileImageRequest, {
        accessKeyId,
        secretAccessKey,
        sessionToken,
      });
      const signedRequest = signer.sign();
      signedRequest.headers.Accept = '*/*';
      signedRequest.cache = 'force-cache';

      try {
        const filename = profileImageFilename;
        const filePermissionKey = `${Config.S3_PROFILE_IMAGES_DIR}/${filename}`;
        const filesDomain = process.env.REACT_APP_FILES_DOMAIN;
        const path = profileImageRequest.path;

        await fetch(`https://${filesDomain}/${path}`, signedRequest);

        let filePermission = {
          id: uuidv4(),
          name: profileImageOriginalFilename,
          file: null,
          type: 'FILE',
          description: '',
          uploadSource: 'profile-page',
        };
        filePermission.file = {
          bucket: Config.S3_BUCKET,
          key: filePermissionKey,
          mimeType: 'image/png',
          region: Config.AWS_REGION,
        };
        profileData.picture = filePermission.file;
        filePermission.key = filePermissionKey;
        await createFilePermissionsForImages([filePermission]);

        await dispatch(
          setUpdateResourcesFromSettingProfile({
            id: profileData.id,
            picture: filePermission.file,
            firstName: profileData.firstName,
          })
        );
        console.log('Profile data to update:', profileData);
      } catch (err) {
        console.log(err);
        profileData.picture = null;
      }
    }

    setSaveLoading(false);
    onClose();
  };

  return (
    <Dialog
      disableBackdropClick
      classes={{ paper: classes.dialogPaper }}
      open={true}
      onClose={onClose}
      aria-labelledby="contact-edit-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader
        icon={<MapIcon />}
        title={t('profile.pictureMust')}
        onClose={onClose}
      />
      <DialogContent className={classes.dialogContent}>
        <Typography>{t('profile.pictureMust_contact_row1')}</Typography>
        <ProfileImage
          loading={loadingProfileImage}
          onProfileImageChange={handleProfileImageChange}
          onProfileImageRotate={handleProfileImageRotate}
          src={
            profileImageDataUri
              ? profileImageDataUri
              : !profileImageUpdate
                ? src
                : ''
          }
          onCroppedImageSave={handleCroppedImageSave}
          setProfileImageModified={setProfileImageModified}
          isDeleteAllowed={false}
        />
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSave}
          id="test-profilebuttonsave"
          disabled={!profileImageModified}
          color="primary"
        >
          {saveLoading ? <ButtonLoading /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
